import React, { useState, useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames';
import type { FC, InputHTMLAttributes, ReactNode } from 'react';

import './SelectField.styles.scss';

type DefaultInputProps = InputHTMLAttributes<HTMLInputElement>;

export type SearchableSelectProps = {
  options: { value: string; label: string | ReactNode; searchString: string }[];
  errorMessage?: string;
  helperText?: string;
  label?: string;
  classNamesInput?: string;
  hasError?: boolean;
  prefixNode?: React.ReactNode;
  suffixNode?: React.ReactNode;
  asterix?: boolean;
  description?: string;
} & DefaultInputProps;

const SelectField: FC<SearchableSelectProps> = ({
  options,
  label,
  errorMessage,
  className,
  classNamesInput,
  prefixNode: prefix,
  suffixNode: suffix,
  disabled,
  asterix,
  helperText,
  description,
  hasError,
  value,
  onChange,
  ...rest
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);

  const computedSelectedOption = useMemo(
    () => options.find((opt) => opt.value === value),
    [value, options],
  );

  const displayValue =
    search.length > 0 ? search : computedSelectedOption?.searchString || '';

  const filteredOptions = useMemo(
    () =>
      options.filter((opt) =>
        opt.searchString.toLowerCase().includes(search.toLowerCase()),
      ),
    [search, options],
  );

  const handleSelect = (option: { value: string }) => {
    onChange(option.value as any);
    setSearch('');
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) setIsOpen(true);
        else
          setHighlightedIndex((prev) =>
            Math.min(prev + 1, filteredOptions.length - 1),
          );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        if (isOpen && filteredOptions[highlightedIndex]) {
          e.preventDefault();
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const highlightedItem = dropdownRef.current.children[
        highlightedIndex
      ] as HTMLElement;
      highlightedItem?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={classNames('select-field', className)}>
      {label && (
        <label htmlFor={rest.id || rest.name}>
          {label}
          {asterix && <span className="select-field__asterix"> *</span>}
        </label>
      )}

      <div className={classNames('select-field__container', 'ncoded-input')}>
        {prefix && <div className="select-field__prefix">{prefix}</div>}
        <input
          {...rest}
          ref={inputRef}
          value={displayValue}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearch('');
          }}
          onKeyDown={handleKeyDown}
          className={classNames('select-field__field', classNamesInput)}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls="select-field-listbox"
        />
        {suffix && <div className="select-field__suffix">{suffix}</div>}
      </div>

      {isOpen && (
        <ul
          ref={dropdownRef}
          className="select-field__dropdown"
          id="select-field-listbox"
          role="listbox"
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={option.value}
                onClick={() => handleSelect(option)}
                className={classNames('select-field__option', {
                  'select-field__option--highlighted':
                    index === highlightedIndex,
                })}
                role="option"
                aria-selected={computedSelectedOption?.value === option.value}
                id={`select-field__option-${index}`}
              >
                {option.label}
              </li>
            ))
          ) : (
            <li
              className="select-field__option select-field__option--no-results"
              role="option"
              aria-selected="false"
            >
              No results found
            </li>
          )}
        </ul>
      )}

      {description && (
        <div className="select-field__description">{description}</div>
      )}
      {hasError && (
        <small className="select-field__error-message">{errorMessage}</small>
      )}
    </div>
  );
};

export default SelectField;
